<template>
  <div class="buy-x-get-y-rule">
    <!-- SECTION 1: REQUIRED PURCHASE -->
    <div class="rule-section">
      <p class="rule-section-title text-white">Required purchase</p>

      <div class="row">
        <div class="col-lg-8 col-md-7 col-12">
          <base-input label="What customer must buy">
            <el-select
              v-model="localConfig.required_items.item_type"
              class="select-primary"
              placeholder="Select item type"
              @change="handleRequiredItemTypeChange"
            >
              <el-option class="select-primary" value="games" label="Events"></el-option>
              <el-option class="select-primary" value="items" label="Merchandise"></el-option>
              <el-option class="select-primary" value="gift_cards" label="Gift cards"></el-option>
              <el-option class="select-primary" value="any" label="Any item type"></el-option>
            </el-select>
          </base-input>
        </div>
        <div class="col-lg-4 col-md-5 col-12 mt-3 mt-md-0">
          <base-input label="How many they must buy">
            <el-input-number
              v-model="localConfig.required_items.min_quantity"
              :min="1"
              :max="999"
              @change="emitUpdate"
            ></el-input-number>
          </base-input>
        </div>
      </div>

      <div class="row" v-if="localConfig.required_items.item_type !== 'any'">
        <div class="col-12">
          <div class="toggle-row">
            <el-switch
              v-model="useSpecificRequiredItems"
              active-color="#e14eca"
              @change="handleRequiredItemsToggle"
            ></el-switch>
            <span class="toggle-label">Limit to specific {{ getItemTypeLabel(localConfig.required_items.item_type) }}</span>
          </div>
        </div>
      </div>

      <div class="row" v-if="localConfig.required_items.item_type !== 'any' && useSpecificRequiredItems">
        <div class="col-12">
          <base-input :label="`Select ${getItemTypeLabel(localConfig.required_items.item_type)}`">
            <el-select
              v-model="localConfig.required_items.item_ids"
              class="select-primary"
              multiple
              filterable
              clearable
              placeholder="Choose items"
              value-key="id"
              @change="emitUpdate"
            >
              <el-option
                v-for="item in getRequiredItemOptions()"
                :key="item.id"
                class="select-primary"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="row" v-if="localConfig.required_items.item_ids.length > 1">
        <div class="col-12">
          <fieldset>
            <legend class="sr-only">If multiple items selected</legend>
            <base-input label="If multiple items selected">
              <el-radio-group v-model="localConfig.required_items.match_mode" @change="emitUpdate">
                <el-radio label="any">
                  <strong>Any</strong> - Customer needs at least one item from the list
                </el-radio>
                <br />
                <el-radio label="all">
                  <strong>All</strong> - Customer needs all items from the list
                </el-radio>
              </el-radio-group>
            </base-input>
          </fieldset>
        </div>
      </div>

      <div class="row" v-if="showRequiredUnitToggle">
        <div class="col-12">
          <base-input label="Count events as">
            <segmented-toggle
              :value="localConfig.required_items.unit"
              :options="unitOptions"
              size="sm"
              @input="handleRequiredUnitChange"
            ></segmented-toggle>
            <info-hint variant="neutral" class="mt-2">
              {{ requiredUnitHint }}
            </info-hint>
          </base-input>
        </div>
      </div>
    </div>

    <!-- SECTION 2: REWARD -->
    <div class="rule-section mt-3">
      <p class="rule-section-title text-white">Discount reward</p>

      <div class="row">
        <div class="col-12">
          <base-input label="What gets discounted">
            <el-select
              v-model="localConfig.discounted_items.item_type"
              class="select-primary"
              placeholder="Select item type"
              @change="handleDiscountedItemTypeChange"
            >
              <el-option class="select-primary" value="games" label="Events"></el-option>
              <el-option class="select-primary" value="items" label="Merchandise"></el-option>
              <el-option class="select-primary" value="gift_cards" label="Gift cards"></el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <base-input
            :label="`Which ${getItemTypeLabel(localConfig.discounted_items.item_type)}`"
            :error="validationErrors.discounted_items"
          >
            <el-select
              v-model="localConfig.discounted_items.item_ids"
              class="select-primary"
              :class="{ 'is-invalid': validationErrors.discounted_items }"
              multiple
              filterable
              clearable
              placeholder="Select items that will receive the discount"
              value-key="id"
              @change="handleDiscountedItemsChange"
            >
              <el-option
                v-for="item in getDiscountedItemOptions()"
                :key="item.id"
                class="select-primary"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <!-- Overlap warning -->
      <div v-if="hasOverlappingItems" class="overlap-warning mb-3">
        <i class="tim-icons icon-alert-circle-exc mr-2"></i>
        <span>Some items appear in both sections. Make sure this is intentional (e.g., "Buy 2, get 1 free" on same item).</span>
      </div>

      <!-- Discount Configuration -->
      <div class="row">
        <div class="col-lg-4 col-md-6 col-12">
          <base-input label="Discount type">
            <el-select
              v-model="localConfig.discounted_items.discount_type"
              class="select-primary"
              placeholder="Select discount type"
              @change="emitUpdate"
            >
              <el-option class="select-primary" value="free" label="Free"></el-option>
              <el-option class="select-primary" value="percentage" label="Percentage off"></el-option>
              <el-option class="select-primary" value="fixed_amount" label="Fixed amount off"></el-option>
            </el-select>
          </base-input>
        </div>
        <div class="col-lg-4 col-md-6 col-12 mt-3 mt-md-0" v-if="localConfig.discounted_items.discount_type !== 'free'">
          <base-input
            :label="discountValueLabel"
            :error="validationErrors.discount_value"
          >
            <el-input-number
              v-model="localConfig.discounted_items.discount_value"
              :min="1"
              :max="localConfig.discounted_items.discount_type === 'percentage' ? 100 : 99999"
              :precision="localConfig.discounted_items.discount_type === 'percentage' ? 0 : 2"
              @change="emitUpdate"
            ></el-input-number>
          </base-input>
        </div>
        <div class="col-lg-4 col-md-6 col-12 mt-3 mt-lg-0">
          <base-input label="Max items to discount">
            <el-input-number
              v-model="localConfig.discounted_items.max_quantity"
              :min="1"
              :max="99"
              @change="emitUpdate"
            ></el-input-number>
          </base-input>
          <small class="text-muted d-block mt-n2">Limit how many items receive this discount</small>
        </div>
      </div>

      <div class="row" v-if="showDiscountedUnitToggle">
        <div class="col-12">
          <base-input label="Count events as">
            <segmented-toggle
              :value="localConfig.discounted_items.unit"
              :options="unitOptions"
              size="sm"
              @input="handleDiscountedUnitChange"
            ></segmented-toggle>
            <info-hint variant="neutral" class="mt-2">
              {{ discountedUnitHint }}
            </info-hint>
          </base-input>
        </div>
      </div>
    </div>

    <!-- SECTION 3: ADVANCED OPTIONS (commented out for now)
    <div class="rule-section mt-3">
      <p class="rule-section-title text-white">Advanced options</p>

      <div class="row">
        <div class="col-lg-6 col-12">
          <base-input label="If this promo also has a general discount">
            <el-select
              v-model="localConfig.discounted_items.discount_mode"
              class="select-primary"
              placeholder="Select discount mode"
              @change="emitUpdate"
            >
              <el-option class="select-primary" value="override" label="Use the better discount">
                <div>
                  <strong>Use the better discount</strong>
                  <div class="text-muted small">Customer gets whichever discount is higher</div>
                </div>
              </el-option>
              <el-option class="select-primary" value="additive" label="Stack discounts">
                <div>
                  <strong>Stack discounts</strong>
                  <div class="text-muted small">Both discounts apply together</div>
                </div>
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <base-input label="If reward items aren't in cart yet">
            <div class="radio-options">
              <label class="radio-option" :class="{ active: localConfig.discounted_items.auto_apply_behavior === 'allow_promo' }">
                <input
                  type="radio"
                  v-model="localConfig.discounted_items.auto_apply_behavior"
                  value="allow_promo"
                  @change="emitUpdate"
                />
                <span class="radio-content">
                  <span class="radio-title">Allow promo</span>
                  <span class="radio-desc">Discount applies when items are added</span>
                </span>
              </label>
              <label class="radio-option" :class="{ active: localConfig.discounted_items.auto_apply_behavior === 'block_promo' }">
                <input
                  type="radio"
                  v-model="localConfig.discounted_items.auto_apply_behavior"
                  value="block_promo"
                  @change="emitUpdate"
                />
                <span class="radio-content">
                  <span class="radio-title">Require items first</span>
                  <span class="radio-desc">Reject code until items in cart</span>
                </span>
              </label>
              <label class="radio-option" :class="{ active: localConfig.discounted_items.auto_apply_behavior === 'show_message' }">
                <input
                  type="radio"
                  v-model="localConfig.discounted_items.auto_apply_behavior"
                  value="show_message"
                  @change="emitUpdate"
                />
                <span class="radio-content">
                  <span class="radio-title">Show reminder</span>
                  <span class="radio-desc">Accept code and display message</span>
                </span>
              </label>
            </div>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <base-input label="Custom error message (optional)">
            <el-input
              v-model="localConfig.error_message"
              type="textarea"
              :rows="2"
              maxlength="200"
              show-word-limit
              placeholder="e.g., Add a game booking to unlock this discount"
              @input="emitUpdate"
            ></el-input>
          </base-input>
          <small class="d-block mt-n2">Shown to customer when this rule is not met</small>
        </div>
      </div>
    </div>
    -->

  </div>
</template>

<script>
import { BaseInput } from 'src/components';

export default {
  name: 'BuyXGetYRule',
  components: {
    BaseInput
  },
  props: {
    ruleConfig: {
      type: Object,
      required: true
    },
    availableGames: {
      type: Array,
      default: () => []
    },
    availableItems: {
      type: Array,
      default: () => []
    },
    availableGiftCards: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      useSpecificRequiredItems: false,
      touched: {
        discountedItems: false
      },
      unitOptions: [
        { value: 'booking', label: 'Whole booking' },
        { value: 'ticket', label: 'Per ticket' }
      ],
      localConfig: {
        required_items: {
          item_type: 'games',
          item_ids: [],
          min_quantity: 1,
          match_mode: 'any',
          unit: 'booking'
        },
        discounted_items: {
          item_type: 'items',
          item_ids: [],
          discount_mode: 'additive',
          discount_type: 'free',
          discount_value: 100,
          max_quantity: 1,
          auto_apply_behavior: 'block_promo',
          unit: 'booking'
        },
        error_message: ''
      }
    };
  },
  computed: {
    discountValueLabel() {
      return this.localConfig.discounted_items.discount_type === 'percentage'
        ? 'Percent off'
        : 'Amount off ($)';
    },
    validationErrors() {
      const errors = {};

      if (this.touched.discountedItems && this.localConfig.discounted_items.item_ids.length === 0) {
        errors.discounted_items = 'Select at least one item to discount';
      }

      if (this.localConfig.discounted_items.discount_type !== 'free') {
        if (this.localConfig.discounted_items.discount_value <= 0) {
          errors.discount_value = 'Must be greater than 0';
        }
        if (this.localConfig.discounted_items.discount_type === 'percentage' &&
            this.localConfig.discounted_items.discount_value > 100) {
          errors.discount_value = 'Cannot exceed 100%';
        }
      }

      return errors;
    },
    isValid() {
      return Object.keys(this.validationErrors).length === 0 &&
             this.localConfig.discounted_items.item_ids.length > 0;
    },
    hasOverlappingItems() {
      if (this.localConfig.required_items.item_type !== this.localConfig.discounted_items.item_type) {
        return false;
      }
      const reqIds = this.localConfig.required_items.item_ids || [];
      const discIds = this.localConfig.discounted_items.item_ids || [];
      return reqIds.some(id => discIds.includes(id));
    },
    showRequiredUnitToggle() {
      const t = this.localConfig.required_items.item_type;
      return t === 'games' || t === 'any';
    },
    showDiscountedUnitToggle() {
      return this.localConfig.discounted_items.item_type === 'games';
    },
    requiredUnitHint() {
      return this.localConfig.required_items.unit === 'ticket'
        ? 'Each participant counts toward the requirement. A booking for 4 participants counts as 4.'
        : 'Each booking counts as one, regardless of how many participants are on it.';
    },
    discountedUnitHint() {
      return this.localConfig.discounted_items.unit === 'ticket'
        ? 'The discount applies to a single ticket within a booking, not the whole group.'
        : 'The discount applies to the entire booking, covering all participants on it.';
    }
  },
  watch: {
    ruleConfig: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.localConfig = {
            required_items: { ...this.localConfig.required_items, ...newVal.required_items },
            discounted_items: { ...this.localConfig.discounted_items, ...newVal.discounted_items },
            error_message: newVal.error_message || ''
          };
          // Sync toggle state based on whether specific items are selected
          this.useSpecificRequiredItems = newVal.required_items?.item_ids?.length > 0;
        }
      }
    }
  },
  methods: {
    emitUpdate() {
      this.$emit('update', JSON.parse(JSON.stringify(this.localConfig)));
    },
    handleRequiredItemTypeChange() {
      this.localConfig.required_items.item_ids = [];
      this.useSpecificRequiredItems = false;
      if (!this.showRequiredUnitToggle) {
        this.localConfig.required_items.unit = 'booking';
      }
      this.emitUpdate();
    },
    handleRequiredUnitChange(value) {
      this.localConfig.required_items.unit = value;
      this.emitUpdate();
    },
    handleDiscountedUnitChange(value) {
      this.localConfig.discounted_items.unit = value;
      this.emitUpdate();
    },
    handleRequiredItemsToggle(enabled) {
      if (!enabled) {
        this.localConfig.required_items.item_ids = [];
        this.emitUpdate();
      }
    },
    handleDiscountedItemTypeChange() {
      this.localConfig.discounted_items.item_ids = [];
      this.touched.discountedItems = false;
      if (!this.showDiscountedUnitToggle) {
        this.localConfig.discounted_items.unit = 'booking';
      }
      this.emitUpdate();
    },
    handleDiscountedItemsChange() {
      this.touched.discountedItems = true;
      this.emitUpdate();
    },
    getRequiredItemOptions() {
      switch (this.localConfig.required_items.item_type) {
        case 'games':
          return this.availableGames;
        case 'items':
          return this.availableItems;
        case 'gift_cards':
          return this.availableGiftCards;
        default:
          return [];
      }
    },
    getDiscountedItemOptions() {
      switch (this.localConfig.discounted_items.item_type) {
        case 'games':
          return this.availableGames;
        case 'items':
          return this.availableItems;
        case 'gift_cards':
          return this.availableGiftCards;
        default:
          return [];
      }
    },
    getItemTypeLabel(type) {
      const labels = {
        'games': 'events',
        'items': 'merchandise',
        'gift_cards': 'gift cards',
        'any': 'items'
      };
      return labels[type] || type;
    }
  }
};
</script>

<style scoped>
/* Section headers */
.rule-section {
  padding: 1rem 1.25rem;
  background: rgba(128, 128, 128, 0.08);
  border-radius: 0.375rem;
  border-left: 3px solid #e14eca;
  margin-bottom: 1rem;
}

.rule-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1rem 0;
  color: #fff;
}

/* Improve label legibility */
.buy-x-get-y-rule >>> label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.buy-x-get-y-rule >>> .form-group label {
  color: rgba(255, 255, 255, 0.8);
}

/* Optional text in labels */
.buy-x-get-y-rule .text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Placeholder text */
.buy-x-get-y-rule >>> .el-input__inner::placeholder,
.buy-x-get-y-rule >>> .el-select .el-input__inner::placeholder,
.buy-x-get-y-rule >>> .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

/* Select input text */
.buy-x-get-y-rule >>> .el-input__inner,
.buy-x-get-y-rule >>> .el-textarea__inner {
  color: #fff;
}

/* Helper text below fields */
.buy-x-get-y-rule small {
  color: rgba(255, 255, 255, 0.6);
}


/* Overlap warning */
.overlap-warning {
  background: rgba(255, 141, 114, 0.15);
  border: 1px solid rgba(255, 141, 114, 0.4);
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 13px;
  color: #ff8d72;
  display: flex;
  align-items: flex-start;
}

.overlap-warning i {
  margin-top: 2px;
  flex-shrink: 0;
}

/* Screen reader only */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Radio button styling for match_mode */
.buy-x-get-y-rule >>> .el-radio__input.is-checked .el-radio__inner {
  border-color: #e14eca;
  background: #e14eca;
}

.buy-x-get-y-rule >>> .el-radio__label strong {
  color: #fff;
}

/* Custom radio options */
.radio-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  padding: 8px 10px;
  border: 1px solid #344675;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  margin: 0;
}

.radio-option:hover {
  border-color: #e14eca;
}

.radio-option.active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.08);
}

.radio-option input[type="radio"] {
  margin: 2px 8px 0 0;
  accent-color: #e14eca;
  flex-shrink: 0;
}

.radio-content {
  display: flex;
  flex-direction: column;
}

.radio-title {
  font-weight: 500;
  color: #fff;
  font-size: 13px;
  line-height: 1.2;
}

.radio-desc {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 1px;
  line-height: 1.3;
}

/* Toggle row */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.toggle-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

/* Mobile responsiveness */
@media (max-width: 767px) {
  .buy-x-get-y-rule >>> .el-input-number {
    width: 100%;
  }

  .buy-x-get-y-rule >>> .el-input-number .el-input__inner {
    padding-left: 50px;
    padding-right: 50px;
  }

  .buy-x-get-y-rule >>> .el-input-number__decrease,
  .buy-x-get-y-rule >>> .el-input-number__increase {
    width: 44px;
  }

  .buy-x-get-y-rule >>> .el-radio {
    margin-bottom: 16px;
  }
}
</style>

<style>
/* Light-mode overrides */
.white-content .buy-x-get-y-rule .rule-section-title {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .buy-x-get-y-rule label,
.white-content .buy-x-get-y-rule .form-group label {
  color: rgba(0, 0, 0, 0.75);
}

.white-content .buy-x-get-y-rule .toggle-label {
  color: rgba(0, 0, 0, 0.75);
}

.white-content .buy-x-get-y-rule small {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .buy-x-get-y-rule .text-muted {
  color: rgba(0, 0, 0, 0.55) !important;
}

.white-content .buy-x-get-y-rule .el-input__inner::placeholder,
.white-content .buy-x-get-y-rule .el-select .el-input__inner::placeholder,
.white-content .buy-x-get-y-rule .el-textarea__inner::placeholder {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .buy-x-get-y-rule .el-input__inner,
.white-content .buy-x-get-y-rule .el-textarea__inner {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .buy-x-get-y-rule .el-radio__label strong {
  color: rgba(0, 0, 0, 0.85);
}
</style>

